/**
 * Payout details review (`docs/30_PARTNER_PORTAL.md` §8.3).
 *
 * * **Queue** — partners whose payout details wait for a person (submitted or
 *   resubmitted), or with *Needs attention* also those an automatic check
 *   handed to staff since.
 * * **Review** — one partner: what was typed (masked), each document beside
 *   what was read from it and how it compared, the automatic checks' findings,
 *   and the document itself. Then: verify, or ask for a correction with a
 *   reason the partner reads (per document too); record the PAN's operative
 *   status found by hand.
 * * **Providers** — which vendor checks a PAN or a bank account, with its
 *   credentials (kept encrypted; a secret is never shown again).
 *
 * Automatic checks only add evidence. Nothing here is decided by them: a
 * person verifies, and every decision is audited with its time.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, FileText } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  DetailField,
  DetailGrid,
  EmptyState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  Tabs,
  Textarea,
  api,
  formatDateTime,
  toast,
  useCan,
  useFileUrl,
} from '@connect/web-shared';

import {
  CHECK_LABELS,
  CHECK_STATUS,
  DETAILS_STATUS,
  FIELD_LABELS,
  MATCH,
  checkKeys,
  checksApi,
  type Finding,
  type ProviderKind,
  type ProviderKindSettings,
  type Review,
  type ReviewCheck,
  type ReviewDocument,
} from './checks-api';
import { Chip, Failed, ReasonDialog, errorText } from './shared';

// ════════════════════════════════════════════════════════════
// The queue
// ════════════════════════════════════════════════════════════
export function PayoutChecksPage() {
  const [params, setParams] = useSearchParams();
  const canManage = useCan('partners.verification.manage');
  const tab = params.get('tab') === 'providers' && canManage ? 'providers' : params.get('tab') === 'attention' ? 'attention' : 'waiting';
  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title="Payout details review"
        description="Partners' bank and tax details, the documents that prove them, and what the automatic checks found."
        tabs={
          <Tabs
            value={tab}
            onChange={(id) => setParams({ tab: id }, { replace: true })}
            items={[
              { id: 'waiting', label: 'To review' },
              { id: 'attention', label: 'Needs attention' },
              ...(canManage ? [{ id: 'providers', label: 'Providers' }] : []),
            ]}
          />
        }
      />
      {tab === 'providers' ? <Providers /> : <Queue state={tab} />}
    </div>
  );
}

function Queue({ state }: { state: 'waiting' | 'attention' }) {
  const navigate = useNavigate();
  const queue = useQuery({ queryKey: checkKeys.queue(state), queryFn: () => checksApi.queue(state) });
  if (queue.isPending) return <LoadingState label="Loading the queue" />;
  if (queue.isError) return <Failed error={queue.error} onRetry={() => void queue.refetch()} />;
  if (!queue.data.length) {
    return (
      <EmptyState
        title="Nothing to review"
        description={
          state === 'waiting'
            ? 'When a partner saves or corrects its payout details, it appears here.'
            : 'No partner has details, a document or a check waiting for a person.'
        }
      />
    );
  }
  return (
    <Card>
      <ul className="divide-y divide-line">
        {queue.data.map((row) => (
          <li key={row.public_id}>
            <button
              type="button"
              onClick={() => navigate(`/partners-checks/${row.public_id}`)}
              className="flex w-full flex-wrap items-center gap-3 px-5 py-3 text-left hover:bg-surface-2"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-heading">{row.name}</span>
                <span className="block text-xs text-muted">
                  {row.reference} · submitted {formatDateTime(row.submitted_at)}
                </span>
              </span>
              {row.for_review ? <Badge tone="warning">{row.for_review} for review</Badge> : null}
              <Chip value={row.details_status} map={DETAILS_STATUS} />
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════
// One partner's review
// ════════════════════════════════════════════════════════════
export function PayoutReviewPage() {
  const { id = '' } = useParams();
  const review = useQuery({
    queryKey: checkKeys.review(id),
    queryFn: () => checksApi.review(id),
    // While a check runs in the background, look again now and then.
    refetchInterval: (query) => {
      const data = query.state.data;
      const busy = [...(data?.checks ?? []), ...(data?.documents ?? [])].some(
        (x) => x.status === 'pending' || x.status === 'processing',
      );
      return busy ? 10_000 : false;
    },
  });
  if (review.isPending) return <LoadingState label="Loading the review" />;
  if (review.isError) return <Failed error={review.error} onRetry={() => void review.refetch()} />;
  return <ReviewBody data={review.data} />;
}

function useRefreshReview(id: string) {
  const queryClient = useQueryClient();
  return async (data?: Review) => {
    if (data) queryClient.setQueryData(checkKeys.review(id), data);
    await queryClient.invalidateQueries({ queryKey: ['partners'] });
  };
}

function ReviewBody({ data }: { data: Review }) {
  const id = data.partner.public_id;
  const refresh = useRefreshReview(id);
  const d = data.details;
  const [correcting, setCorrecting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [note, setNote] = useState('');
  const approve = useMutation({
    mutationFn: () => checksApi.approve(id, note.trim() || null),
    onSuccess: async (result) => {
      toast.success('Payout details verified');
      setApproving(false);
      await refresh(result);
    },
  });
  const waiting = d.status === 'unverified';
  const failedDocs = data.documents.filter((doc) => doc.status === 'failed').length;

  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title={data.partner.name}
        description={`${data.partner.reference} · payout details`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/partners-checks" className="inline-flex items-center gap-1 text-sm text-muted hover:text-body">
              <ArrowLeft className="size-4" aria-hidden /> Queue
            </Link>
            <Link to={`/partners/${id}`} className="inline-flex items-center gap-1 text-sm text-muted hover:text-body">
              Partner <ExternalLink className="size-3.5" aria-hidden />
            </Link>
          </div>
        }
      />

      <Card>
        <CardHeader
          title="What the partner typed"
          description="Masked here as everywhere. The documents below are the evidence."
          actions={<Chip value={d.status} map={DETAILS_STATUS} />}
        />
        <CardBody className="space-y-4">
          {d.status === 'correction_requested' ? (
            <Alert tone="warning" title="Correction asked">
              {d.correction_reason}
            </Alert>
          ) : null}
          <DetailGrid>
            <DetailField label="Legal name">{d.legal_name}</DetailField>
            <DetailField label="PAN">
              {d.pan ?? 'Not given — TDS at the rate without a PAN'}
              {d.pan_operative === false ? <Badge tone="danger">Inoperative</Badge> : null}
              {d.pan_operative === true ? <Badge tone="success">Operative</Badge> : null}
            </DetailField>
            <DetailField label="GST">{d.gst_registered ? d.gstin : 'Not registered'}</DetailField>
            <DetailField label="Account holder">{d.bank_holder}</DetailField>
            <DetailField label="Account">{d.bank_account}</DetailField>
            <DetailField label="IFSC">{d.bank_ifsc}</DetailField>
            <DetailField label="Bank">{d.bank_name}</DetailField>
            <DetailField label="Branch">
              {[d.bank_branch, d.bank_city, d.bank_state].filter(Boolean).join(', ') || null}
            </DetailField>
            <DetailField label="Submitted">{formatDateTime(d.submitted_at)}</DetailField>
            <DetailField label="Verified">
              {d.verified_at ? `${formatDateTime(d.verified_at)}${d.verified_by ? ` by ${d.verified_by}` : ''}` : null}
            </DetailField>
          </DetailGrid>
          <Can permission="partners.verification.approve">
            <div className="flex flex-wrap gap-2">
              {waiting ? (
                <Button onClick={() => setApproving(true)} disabled={failedDocs > 0}>
                  Verify payout details
                </Button>
              ) : null}
              {waiting || d.status === 'verified' ? (
                <Button variant="secondary" onClick={() => setCorrecting(true)}>
                  Ask for a correction
                </Button>
              ) : null}
              {d.pan ? <PanStatus id={id} /> : null}
            </div>
            {failedDocs ? (
              <p className="text-sm text-muted">
                A document is marked for correction: ask the partner to correct it, or accept it below.
              </p>
            ) : null}
          </Can>
        </CardBody>
      </Card>

      <Checks checks={data.checks} providers={data.providers} />

      <Card>
        <CardHeader
          title="Documents"
          description={
            data.reading_documents
              ? 'Each document is read and compared with what was typed. Open it to see it yourself.'
              : 'Documents are not read automatically on this installation — compare each with the details by eye.'
          }
        />
        <CardBody className="space-y-4">
          {data.required
            .filter((r) => !data.documents.some((doc) => doc.kind === r.kind))
            .map((r) => (
              <Alert key={r.kind} tone="warning">
                Not given: {r.label}.
              </Alert>
            ))}
          {data.documents.length === 0 ? <p className="text-sm text-muted">No documents yet.</p> : null}
          {data.documents.map((doc) => (
            <DocumentReview key={doc.public_id} partnerId={id} doc={doc} />
          ))}
          {data.earlier_documents.length ? (
            <details className="text-sm">
              <summary className="cursor-pointer text-muted">
                {data.earlier_documents.length} earlier document{data.earlier_documents.length === 1 ? '' : 's'}, replaced or removed
              </summary>
              <ul className="mt-2 space-y-1">
                {data.earlier_documents.map((doc) => (
                  <li key={doc.public_id} className="flex flex-wrap items-center gap-2">
                    <FileText className="size-4 text-muted" aria-hidden />
                    <span>{doc.label}</span>
                    <span className="text-muted">
                      {doc.filename} · uploaded {formatDateTime(doc.uploaded_at)} · replaced {formatDateTime(doc.superseded_at)}
                    </span>
                    <OpenFile partnerId={id} doc={doc} />
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </CardBody>
      </Card>

      <Modal
        open={approving}
        onClose={() => setApproving(false)}
        title="Verify these payout details?"
        description="Payouts can then be approved to this account. Anything an automatic check left for review is recorded as checked by you."
        footer={
          <>
            <Button variant="secondary" onClick={() => setApproving(false)}>
              Cancel
            </Button>
            <Button loading={approve.isPending} onClick={() => approve.mutate()}>
              Verify
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {approve.isError ? <Alert tone="danger">{errorText(approve.error)}</Alert> : null}
          <Textarea label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
        </div>
      </Modal>
      <CorrectionDialog open={correcting} data={data} onClose={() => setCorrecting(false)} />
    </div>
  );
}

function Checks({ checks, providers }: { checks: ReviewCheck[]; providers: Review['providers'] }) {
  return (
    <Card>
      <CardHeader title="Automatic checks" description="They confirm or hand over to you — they never refuse anybody." />
      <CardBody>
        {checks.length === 0 ? (
          <p className="text-sm text-muted">No details submitted yet.</p>
        ) : (
          <ul className="divide-y divide-line">
            {checks.map((check) => (
              <li key={check.public_id} className="flex flex-wrap items-start gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-heading">{CHECK_LABELS[check.kind]}</p>
                  <p className="text-sm text-muted">{describeCheck(check, providers)}</p>
                  {check.reason ? <p className="text-sm text-muted">Told the partner: “{check.reason}”</p> : null}
                  {check.reviewed_by ? (
                    <p className="text-xs text-muted">
                      Accepted by {check.reviewed_by}, {formatDateTime(check.reviewed_at)}
                    </p>
                  ) : null}
                </div>
                <Chip value={check.status} map={CHECK_STATUS} />
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function describeCheck(check: ReviewCheck, providers: Review['providers']): string {
  const r = check.result;
  if (check.status === 'pending' || check.status === 'processing') {
    return check.attempts ? `Trying again (attempt ${check.attempts + 1}).` : 'Waiting to run.';
  }
  if (r.superseded) return 'The details changed before this ran.';
  if (r.error) return `Not completed: ${String(r.error)}. Check it by hand.`;
  if (r.configured === false) {
    return providers[check.kind as ProviderKind]
      ? 'No provider was chosen when this ran. Check it by hand.'
      : 'No gateway is set up for this check yet (Providers tab). Check it by hand.';
  }
  if (check.kind === 'ifsc') {
    if (r.found === false) return 'No such IFSC in the branch directory.';
    const typed = r.typed_bank && r.typed_bank !== r.bank ? ` The partner typed “${String(r.typed_bank)}”.` : '';
    return `${String(r.bank)}, ${String(r.branch)}${r.city ? `, ${String(r.city)}` : ''}${r.state ? `, ${String(r.state)}` : ''}.${typed}`;
  }
  const parts: string[] = [];
  if (r.found === false) parts.push('Not found in the records.');
  if (r.name_match) parts.push(`Name: ${MATCH[r.name_match as keyof typeof MATCH]?.label ?? String(r.name_match)}.`);
  if (r.operative === false) parts.push('Inoperative — taxed as no PAN.');
  if (r.operative === true) parts.push('Operative.');
  if (r.active === false) parts.push('Registration cancelled or suspended.');
  if (r.active === true) parts.push('Registration active.');
  return parts.join(' ') || 'Done.';
}

function DocumentReview({ partnerId, doc }: { partnerId: string; doc: ReviewDocument }) {
  const refresh = useRefreshReview(partnerId);
  const [rejecting, setRejecting] = useState(false);
  const decide = useMutation({
    mutationFn: ({ verdict, reason }: { verdict: 'verified' | 'failed'; reason: string | null }) =>
      checksApi.decideDocument(partnerId, doc.public_id, verdict, reason),
    onSuccess: async (result) => {
      setRejecting(false);
      await refresh(result);
    },
    onError: (e) => toast.error('Not saved', errorText(e)),
  });
  const isImage = doc.content_type.startsWith('image/');
  const preview = useFileUrl(isImage ? checksApi.filePath(partnerId, doc.public_id) : null);
  const fields = Object.entries(doc.findings).filter(
    (entry): entry is [string, Finding] => typeof entry[1] === 'object' && entry[1] !== null && 'result' in entry[1],
  );
  const note =
    doc.findings.read === false
      ? doc.findings.unreadable
        ? 'The reader could not read this file.'
        : 'Not read automatically.'
      : doc.findings.empty
        ? 'Nothing recognisable was read from it.'
        : typeof doc.findings.error === 'string'
          ? `Not completed: ${doc.findings.error}.`
          : null;

  return (
    <div className="grid gap-4 rounded-card border border-line p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="flex-1 font-medium text-heading">{doc.label}</h3>
          <Chip value={doc.status} map={CHECK_STATUS} />
        </div>
        <p className="text-xs text-muted">
          {doc.filename} · uploaded {formatDateTime(doc.uploaded_at)}
          {doc.reviewed_by ? ` · decided by ${doc.reviewed_by}, ${formatDateTime(doc.reviewed_at)}` : ''}
        </p>
        {fields.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="py-1 font-normal">Field</th>
                <th className="py-1 font-normal">Read from the document</th>
                <th className="py-1 font-normal">Against what was typed</th>
              </tr>
            </thead>
            <tbody>
              {fields.map(([field, finding]) => (
                <tr key={field} className="border-t border-line">
                  <td className="py-1.5">{FIELD_LABELS[field] ?? field}</td>
                  <td className="py-1.5 font-mono text-xs">{finding.read ?? '—'}</td>
                  <td className="py-1.5">
                    <Chip value={finding.result} map={MATCH} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {note ? <p className="text-sm text-muted">{note}</p> : null}
        {doc.reason ? <p className="text-sm text-muted">Told the partner: “{doc.reason}”</p> : null}
        <Can permission="partners.verification.approve">
          <div className="flex flex-wrap gap-2">
            {doc.status !== 'verified' ? (
              <Button size="sm" variant="secondary" loading={decide.isPending} onClick={() => decide.mutate({ verdict: 'verified', reason: null })}>
                Accept document
              </Button>
            ) : null}
            {doc.status !== 'failed' ? (
              <Button size="sm" variant="secondary" onClick={() => setRejecting(true)}>
                Ask for another
              </Button>
            ) : null}
            <OpenFile partnerId={partnerId} doc={doc} />
          </div>
        </Can>
      </div>
      <div className="flex min-h-40 items-center justify-center rounded-card bg-surface-2 p-2">
        {isImage ? (
          preview.data ? (
            <img src={preview.data} alt={doc.label} className="max-h-96 max-w-full object-contain" />
          ) : preview.isError ? (
            <p className="text-sm text-muted">The picture could not be loaded.</p>
          ) : (
            <p className="text-sm text-muted">Loading…</p>
          )
        ) : (
          <p className="text-sm text-muted">A PDF — open it to read it.</p>
        )}
      </div>
      <ReasonDialog
        open={rejecting}
        title="Ask for another document"
        description="The partner reads your reason. Say what to send instead."
        confirmLabel="Ask the partner"
        busy={decide.isPending}
        error={decide.error}
        onClose={() => setRejecting(false)}
        onConfirm={(reason) => decide.mutate({ verdict: 'failed', reason })}
      />
    </div>
  );
}

function OpenFile({ partnerId, doc }: { partnerId: string; doc: ReviewDocument }) {
  const [opening, setOpening] = useState(false);
  const open = async () => {
    setOpening(true);
    // Fetched with the session's token, then shown from memory: the file has
    // no address of its own.
    const tab = window.open('', '_blank');
    try {
      const file = await api.file(checksApi.filePath(partnerId, doc.public_id));
      const url = URL.createObjectURL(file.blob);
      if (tab) tab.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      tab?.close();
      toast.error('Could not open it', errorText(error));
    } finally {
      setOpening(false);
    }
  };
  return (
    <Button size="sm" variant="ghost" loading={opening} onClick={() => void open()}>
      Open <ExternalLink className="size-3.5" aria-hidden />
    </Button>
  );
}

function CorrectionDialog({ open, data, onClose }: { open: boolean; data: Review; onClose: () => void }) {
  const id = data.partner.public_id;
  const refresh = useRefreshReview(id);
  const [reason, setReason] = useState('');
  const [perDocument, setPerDocument] = useState<Record<string, string>>({});
  const ask = useMutation({
    mutationFn: () =>
      checksApi.requestCorrection(
        id,
        reason.trim(),
        Object.fromEntries(Object.entries(perDocument).filter(([, v]) => v.trim().length >= 3).map(([k, v]) => [k, v.trim()])),
      ),
    onSuccess: async (result) => {
      toast.success('Correction asked for', 'The partner sees your reason in its portal.');
      setReason('');
      setPerDocument({});
      onClose();
      await refresh(result);
    },
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdrop={false}
      title="Ask for a correction"
      description="The partner reads exactly what you write. No payout can be approved until it is corrected and verified."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={ask.isPending} disabled={reason.trim().length < 3} onClick={() => ask.mutate()}>
            Ask the partner
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {ask.isError ? <Alert tone="danger">{errorText(ask.error)}</Alert> : null}
        <Textarea
          label="What to correct"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          hint="For example: the account holder's name must match the name on the cheque."
        />
        {data.documents.length ? <p className="text-sm text-muted">A document to replace? Say why beside it.</p> : null}
        {data.documents.map((doc) => (
          <Input
            key={doc.public_id}
            label={`${doc.label} — ${doc.filename}`}
            value={perDocument[doc.public_id] ?? ''}
            onChange={(e) => setPerDocument((p) => ({ ...p, [doc.public_id]: e.target.value }))}
            placeholder="Leave empty if this one is fine"
          />
        ))}
      </div>
    </Modal>
  );
}

function PanStatus({ id }: { id: string }) {
  const refresh = useRefreshReview(id);
  const [choice, setChoice] = useState<boolean | null>(null);
  const save = useMutation({
    mutationFn: (note: string) => checksApi.setPanStatus(id, choice ?? true, note),
    onSuccess: async (result) => {
      toast.success('PAN status recorded');
      setChoice(null);
      await refresh(result);
    },
  });
  return (
    <>
      <Button variant="ghost" onClick={() => setChoice(true)}>
        Record PAN operative
      </Button>
      <Button variant="ghost" onClick={() => setChoice(false)}>
        Record PAN inoperative
      </Button>
      <ReasonDialog
        open={choice !== null}
        title={choice === false ? 'Record this PAN as inoperative?' : 'Record this PAN as operative?'}
        description={
          choice === false
            ? 'An inoperative PAN is taxed as no PAN: payouts take TDS at the rule’s rate without a PAN until it is recorded operative again.'
            : 'Say how you checked it — for example, the income-tax site’s “Verify your PAN”, with the date.'
        }
        confirmLabel="Record"
        variant={choice === false ? 'danger' : 'primary'}
        busy={save.isPending}
        error={save.error}
        onClose={() => setChoice(null)}
        onConfirm={(note) => save.mutate(note)}
      />
    </>
  );
}

// ════════════════════════════════════════════════════════════
// Providers
// ════════════════════════════════════════════════════════════
function Providers() {
  const settings = useQuery({ queryKey: checkKeys.providers, queryFn: checksApi.providers });
  if (settings.isPending) return <LoadingState label="Loading providers" />;
  if (settings.isError) return <Failed error={settings.error} onRetry={() => void settings.refetch()} />;
  return (
    <div className="space-y-4">
      <ProviderCard
        kind="pan"
        title="PAN verification"
        description="Checks a PAN against the income-tax records: the holder's name and whether it is operative. An inoperative PAN is taxed as no PAN."
        settings={settings.data.pan}
      />
      <ProviderCard
        kind="gstin"
        title="GSTIN verification"
        description="Checks a GST registration: that it exists and is active, and that its legal name is the one typed."
        settings={settings.data.gstin}
      />
      <ProviderCard
        kind="bank_account"
        title="Bank account verification"
        description="A ₹1 test credit to the account, whose answer carries the holder's name as the bank has it."
        settings={settings.data.bank_account}
      />
      <Card>
        <CardHeader title="Reading documents" />
        <CardBody>
          <p className="text-sm text-muted">
            {settings.data.documents.reader
              ? 'Uploaded documents are read by Amazon Textract (Mumbai) and compared with what was typed.'
              : 'Off. Documents go to review unread. Switched on by the installation’s TEXTRACT_ENABLED setting, with its own access key.'}
          </p>
        </CardBody>
      </Card>
    </div>
  );
}

function ProviderCard({
  kind,
  title,
  description,
  settings,
}: {
  kind: ProviderKind;
  title: string;
  description: string;
  settings: ProviderKindSettings;
}) {
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState(settings.provider ?? settings.available[0]?.key ?? '');
  const [values, setValues] = useState<Record<string, string>>({});
  const chosen = settings.available.find((p) => p.key === provider);
  const done = async () => {
    setValues({});
    await queryClient.invalidateQueries({ queryKey: checkKeys.providers });
  };
  const save = useMutation({
    mutationFn: () => checksApi.setProvider(kind, provider, values),
    onSuccess: async () => {
      toast.success('Provider saved');
      await done();
    },
  });
  const clear = useMutation({
    mutationFn: () => checksApi.clearProvider(kind),
    onSuccess: async () => {
      toast.success('Provider removed', 'These checks go to review until another is chosen.');
      await done();
    },
  });
  return (
    <Card>
      <CardHeader
        title={title}
        description={description}
        actions={
          settings.provider ? <Badge tone="success">{settings.provider_label}</Badge> : <Badge tone="neutral">Not configured</Badge>
        }
      />
      <CardBody className="space-y-3">
        {settings.available.length === 0 ? (
          <Alert tone="info" title="Waiting for the KYC gateway">
            The owner is arranging the gateway that will check PANs and GST registrations. Once it is added to
            Connect, choose it here and enter its credentials. Until then this check goes to review, and is done by
            hand.
          </Alert>
        ) : (
          <>
            <Select
              label="Provider"
              value={provider}
              options={settings.available.map((p) => ({ value: p.key, label: p.label }))}
              onChange={(e) => {
                setProvider(e.target.value);
                setValues({});
              }}
            />
            {chosen?.note ? <p className="text-sm text-muted">{chosen.note}</p> : null}
            {chosen?.fields.map((field) => {
              const stored = settings.provider === provider ? settings.values[field.key] : undefined;
              return (
                <Input
                  key={field.key}
                  label={field.label}
                  type={field.secret ? 'password' : 'text'}
                  autoComplete="off"
                  value={values[field.key] ?? (field.secret ? '' : (stored ?? ''))}
                  onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                  placeholder={field.secret && stored ? 'Saved — type to replace' : undefined}
                  hint={field.help ?? undefined}
                />
              );
            })}
            {save.isError ? <Alert tone="danger">{errorText(save.error)}</Alert> : null}
            <div className="flex flex-wrap gap-2">
              <Button loading={save.isPending} onClick={() => save.mutate()} disabled={!provider}>
                Save
              </Button>
              {settings.provider ? (
                <Button variant="ghost" loading={clear.isPending} onClick={() => clear.mutate()}>
                  Remove provider
                </Button>
              ) : null}
            </div>
          </>
        )}
        {settings.updated_at ? <p className="text-xs text-muted">Last changed {formatDateTime(settings.updated_at)}</p> : null}
      </CardBody>
    </Card>
  );
}
